<script lang="ts">
export default {
  name: 'KkSentencesModal',
}
</script>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { KkModal, KkBox, KkLoading } from 'src/biz_spa/components/base'
import { KkInfo } from 'src/biz_spa/components/ui'
import type { ReviewSentence, QSCACategory } from 'src/biz_spa/types'

export interface Props {
  isVisible: boolean
  positive: ReviewSentence[]
  negative: ReviewSentence[]
  qscaCategory: QSCACategory
  isLoading?: boolean
  level?: 1 | 2 | 3 | 4 | 5
}

export interface Emits {
  (key: 'update:isVisible', value: Props['isVisible']): void
}

const props = withDefaults(defineProps<Props>(), { level: 2 })
const emits = defineEmits<Emits>()
const { t } = useI18n()

const isVisibleModel = computed({
  get: () => props.isVisible,
  set: (value) => emits('update:isVisible', value),
})

const title = computed(() => {
  const category = props.qscaCategory.toLowerCase()
  const categoryName = t(`models.analysis.qscaDataTypes.${category}.name`)
  return t('pages.companies.analysis.qscas.sentencesModal.title', { category: categoryName })
})
</script>

<template>
  <KkModal :is-visible="isVisibleModel" :level="2" width="1076px" class="kkSentencesModal" @change="isVisibleModel = false">
    <KkBox :title="title">
      <template #content>
        <KkLoading v-if="props.isLoading" />
        <div v-else :class="$style.container">
          <KkInfo icon="hint">{{ t('pages.companies.analysis.qscas.sentencesModal.hint') }}</KkInfo>

          <div :class="$style.columns">
            <section :class="[$style.section, $style.positive]">
              <component :is="`h${props.level + 1}`" :class="$style.title">{{ t('pages.companies.analysis.qscas.sentencesModal.positive') }}</component>
              <div :class="$style.content">
                <ul v-if="props.positive" :class="$style.list">
                  <li v-for="(item, i) in props.positive" :key="i" :class="$style.item">{{ item.sentence }}</li>
                </ul>
              </div>
            </section>

            <section :class="[$style.section, $style.negative]">
              <component :is="`h${props.level + 1}`" :class="$style.title">{{ t('pages.companies.analysis.qscas.sentencesModal.negative') }}</component>
              <div :class="$style.content">
                <ul v-if="props.negative" :class="$style.list">
                  <li v-for="(item, i) in props.negative" :key="i" :class="$style.item">{{ item.sentence }}</li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </template>
    </KkBox>
  </KkModal>
</template>

<style lang="scss" module>
@import '../../../../../../stylesheets/biz_spa/foundation/var';
@import '../../../../../../stylesheets/biz_spa/foundation/mixin';

.container {
  container: wrapper / inline-size;
  min-height: 264px;
}
.columns {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;
  @include mq(sm) {
    flex-direction: row;
  }
}
.section {
  --color: currentColor;
  width: 100%;
  &.positive {
    --color: #4C959A;
  }
  &.negative {
    --color: #E9446A;
  }
  @include mq(sm) {
    display: flex;
    flex-direction: column;
    width: 50%;
  }
}
.title {
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.5;
  color: #FFF;
  background: var(--color);
  padding: 15px 16px;
}
.content {
  overflow-y: auto;
  border-radius: 4px;
  border: 1px solid var(--color);
  padding: 16px;
  margin-top: 8px;
  @include mq(sm) {
    max-height: 70cqh;
    height: 100%;
  }
}
.item {
  position: relative;
  font-size: 14px;
  line-height: 1.7;
  padding-left: 24px;
  &::before {
    position: absolute;
    top: 6px;
    left: 8px;
    content: '';
    background: var(--color);
    border-radius: 50%;
    width: 8px;
    height: 8px;
  }
  &:not(:first-child) {
    margin-top: 32px;
  }
}
</style>

<style lang="scss" scoped>
.kkSentencesModal {
  :deep(.modal__container) {
    overflow: auto;
  }
}
</style>
